import debug from '@fs/zion-debug'
import chunk from 'lodash.chunk'

import { filterOrdinances, filterOrdinancesByDetailsOwner, getOwnerDate } from '../lib/helpers'

const log = debug('temple:utils')

const BATCH_SIZE = 30

export const extractPidsFromReservationList = (data) => {
  // extract pids from reservation list
  const pids = []
  data.list.reservation.forEach((reservation) => {
    const type = Object.keys(reservation)[0]
    pids.push(reservation[type].primaryPersonId || null)
  })

  // return only unique pids from list
  return Array.from(new Set(pids)).filter(Boolean)
}

/**
 * Transform an array of pids to chunkPids format.
 * @param {Array} pids - An array of pids.
 * @returns {Array} - ChunkPids which is an array of arrays.
 */
export const getChunkPidArray = (pids) => {
  const uniquePids = Array.from(new Set(pids))
  return chunk(uniquePids, BATCH_SIZE)
}

/**
 * Returns an array of arrays of pids in batches of 30.
 * @param {Array} reservations - An Array of original reservations.
 * @returns {Array} - Chunked pids.
 */
export const getPidsFromReservations = (reservations) => {
  const reservation = reservations.map((res) => {
    return { anyKeyHereWillDo: { primaryPersonId: res.id } }
  })
  return chunk(extractPidsFromReservationList({ list: { reservation } }), BATCH_SIZE)
}

export const mungeReservations = ({ allRows, sharedWithTemple, cpUserId }) => {
  log('original', allRows)

  // recaulate is whether or not some of the ordinances have 'Calculating' for status, so then all reservations need to be fetched again to have correct fresh data
  // This is for shared with temple where ordinances that are in a calucating state say they are not shared with temple when they could be (after calculation is done)
  let recalculate = false

  const mungedRows = allRows.map((row) => {
    const ords = ['baptism', 'confirmation', 'initiatory', 'endowment', 'sealingToParents', 'sealingToSpouse']

    let type = 'personal'
    let relationships = []

    const expirationDate = getOwnerDate(row, 'dateExpiry', cpUserId)
    // TW-3205. The row "dateSubmitted" property is the reserved summary date (verified in tree-data).
    // It should be used for "Reserved: " on the Reservations tab.
    // The "assignedDate" property should be used for "Shared: " on the Shared tab.
    const assignedDate = getOwnerDate(row, 'dateAssigned', cpUserId)
    const reservedDate = getOwnerDate(row, 'dateSubmitted', cpUserId)
    const modifiedDate = getOwnerDate(row, 'dateModified', cpUserId)
    const comparableExpirationDate = getOwnerDate(row, 'comparableDateExpiry', cpUserId)
    // TW-3205. The row "comparableDateSubmitted" property is the comparable reserved summary date (verified in tree-data).
    const comparableAssignedDate = getOwnerDate(row, 'comparableDateAssigned', cpUserId)
    const comparableReservedDate = getOwnerDate(row, 'comparableDateSubmitted', cpUserId)
    const comparableModifiedDate = getOwnerDate(row, 'comparableDateModified', cpUserId)

    if (row.sealingToSpouse.status !== 'NotApplicable' && row.sealingToSpouse.status !== 'Calculating') {
      type = 'sealingToSpouse'
      relationships = [
        {
          id: row.sealingToSpouse.relationships.spouseId,
          name: row.sealingToSpouse.relationships.spouseName,
          gender: row.sealingToSpouse.relationships.spouseGender,
          lifespan: row.sealingToSpouse.relationships.spouseLifeSpan,
        },
      ]
    }
    if (row.sealingToParents.status !== 'NotApplicable' && row.sealingToParents.status !== 'Calculating') {
      type = 'sealingToParents'
      relationships = [
        {
          id: row.sealingToParents.relationships.parent1Id,
          name: row.sealingToParents.relationships.parent1Name,
          gender: row.sealingToParents.relationships.parent1Gender,
          lifespan: row.sealingToParents.relationships.parent1LifeSpan,
        },
      ]

      if (row.sealingToParents.relationships.parent2Id) {
        relationships.push({
          id: row.sealingToParents.relationships.parent2Id,
          name: row.sealingToParents.relationships.parent2Name,
          gender: row.sealingToParents.relationships.parent2Gender,
          lifespan: row.sealingToParents.relationships.parent2LifeSpan,
        })
      }
    }

    const isCalculating = ords.some((ord) => row[ord]?.status === 'Calculating')
    if (recalculate === false && isCalculating === true) recalculate = true

    return {
      type,
      expirationDate,
      assignedDate,
      reservedDate,
      modifiedDate,
      comparableExpirationDate,
      comparableAssignedDate,
      comparableReservedDate,
      comparableModifiedDate,
      // Make a msg show if this is a secondary reservation. This is an all or nothing condition.
      // Originally the "alternateReservationDetails" object came from the Reservations service as "secondaryReservationDetails".
      // When the service changed it, we decided to have tree-data give us the new object name ("alternateReservationDetails") to avoid any confusion later on.
      // However, we decided to leave HTML, CSS, and JavaScript references with the term "secondary".
      showCannotShareSecondaryReservationsWarning: !sharedWithTemple && row.alternateReservationDetails,
      // Make a msg show if a reserved person was merged and this patron no longer has the reservation. This is an all or nothing condition.
      showMergedNoLongerValidWarning: row.callerReservationDetails,
      // Make a msg show if a reserved person was merged and this patron no longer has some reservations.
      showMergedSomeNoLongerValidWarning:
        !row.callerReservationDetails &&
        Object.keys(row)
          .filter((key) => ords.includes(key))
          .some((ordinance) => {
            return row[ordinance].callerReservationDetails
          }),
      showUnqualifiedWarning: Object.keys(row)
        .filter((key) => ords.includes(key))
        .every((ordinance) => {
          switch (ordinance) {
            case 'sealingToParents':
              return (
                row[ordinance].whyNotQualifyingReasons.length > 0 || row[ordinance].status.includes('NotApplicable')
              )
            case 'sealingToSpouse':
              return row[ordinance].whyNotQualifyingReasons.length > 0 || row[ordinance].relationships.spouseId === null
            default:
              return row[ordinance].whyNotQualifyingReasons.length > 0
          }
        }),
      relationships,
      pendingTransfer: row.pendingTransfer,
      person: {
        id: row.id,
        name: row.name,
        familyName: row.familyName,
        givenName: row.givenName,
        gender: row.gender,
        lifespan: row.lifespan,
        treeId: row.treeId,
      },
      groupId: row.groupId,
      originalRow: row,
      ordinances: {
        baptism: row.baptism,
        confirmation: row.confirmation,
        endowment: row.endowment,
        sealingToSpouse: row.sealingToSpouse,
        sealingToParents: row.sealingToParents,
        initiatory: row.initiatory,
      },
      disableCheckbox:
        sharedWithTemple && ((row.fullyAssignedToTemple && row.fullyPrinted) || row.alternateReservationDetails),
      calculating: isCalculating,
      cpUserId,
    }
  })

  log('munged', mungedRows)

  if (sharedWithTemple) {
    return {
      data: mungedRows.filter((reservation) => {
        return (
          filterOrdinances(reservation.ordinances, 'assignedToTemple') ||
          reservation.calculating ||
          reservation.showMergedNoLongerValidWarning
        )
      }),
      recalculate,
    }
  }

  return {
    data: mungedRows.filter((reservation) => {
      return (
        !filterOrdinances(reservation.ordinances, 'assignedToTemple') ||
        reservation.calculating ||
        filterOrdinancesByDetailsOwner(reservation.ordinances, 'alternateReservationDetails', cpUserId)
      )
    }),
    recalculate,
  }
}
